import React, { useState } from 'react';
import { Breadcrumb } from '@/components/ui/Breadcrumb';
import { motion, AnimatePresence } from 'motion/react';
import { ArrowLeft, CheckCircle2, Mail, MessageCircle, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BookingWidget } from '@/components/forms/BookingWidget';
import { Button } from '@/components/ui/Button';
import { Helmet } from 'react-helmet-async';
import { trackEvent, trackMailtoClick } from '@/utils/analytics';
import { useSiteSettings } from '@/contexts/SiteSettingsContext';

const WhatsAppIcon = ({ className }: { className?: string }) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    viewBox="0 0 24 24" 
    fill="currentColor" 
    className={className}
  >
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z"/>
  </svg>
);

export function ContactPage() {
  const { t } = useTranslation();
  const { links } = useSiteSettings();
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Simulate form submission
    setIsSubmitted(true);
  };

  return (
    <div className="pt-32 pb-20 px-6 lg:px-12 max-w-7xl mx-auto">
      <Helmet>
        <title>Contact FIVE LENS Enneagram | Get in Touch</title>
        <meta name="description" content="Reach our teams in Cape Town and Greater Philadelphia. Book a discovery call, enquire about certifications, or discuss enterprise solutions." />
        <meta property="og:title" content="Contact FIVE LENS Enneagram | Get in Touch" />
        <meta property="og:description" content="Reach our teams in Cape Town and Greater Philadelphia. Book a discovery call, enquire about certifications, or discuss enterprise solutions." />
        <meta name="twitter:title" content="Contact FIVE LENS Enneagram | Get in Touch" />
        <meta name="twitter:description" content="Reach our teams in Cape Town and Greater Philadelphia. Book a discovery call, enquire about certifications, or discuss enterprise solutions." />
      </Helmet>
      <div className="max-w-6xl mx-auto px-6 lg:px-8 mb-8">
        <Breadcrumb items={[{ label: 'Contact Us' }]} />
      </div>
<div className="max-w-3xl mb-16">
        <h1 className="text-4xl lg:text-5xl font-serif text-ink mb-6">Contact Us</h1>
        <p className="text-lg text-ink/80">
          Whether you are looking to become a certified practitioner or want to bring the FIVE LENS Enneagram to your organisation, we are here to help.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2">
          <BookingWidget ctaPosition="contact_page_main" />
        </div>

        <div className="space-y-8">
          <div className="bg-ground-alt border border-ground-deep p-8">
            <h3 className="text-xl font-serif text-ink mb-6">General Enquiries</h3>
            <div className="space-y-6">
              <a 
                href={`mailto:${links.contactEmail}`}
                onClick={() => trackMailtoClick('contact_page_sidebar')}
                className="flex items-start gap-4 hover:opacity-80 transition-opacity"
              >
                <Mail className="w-5 h-5 text-rule-3 shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-bold text-ink text-sm">Email</h4>
                  <p className="text-ink/70 text-sm">{links.contactEmail}</p>
                </div>
              </a>
              <div className="flex items-start gap-4">
                <MessageCircle className="w-5 h-5 text-rule-3 shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-bold text-ink text-sm mb-4">WhatsApp</h4>
                  <ul className="space-y-4">
                    <li>
                      <a href="https://wa.me/27833428122" target="_blank" rel="noreferrer" className="group block hover:opacity-80 transition-opacity">
                        <span className="text-sm font-bold text-ink group-hover:text-rule-3 transition-colors">Colin (Knowledge)</span>
                        <span className="text-sm text-ink/70 flex items-center gap-1.5 mt-0.5"><WhatsAppIcon className="w-3.5 h-3.5" />Message on WhatsApp</span>
                      </a>
                    </li>
                    <li>
                      <a href="https://wa.me/27832417048" target="_blank" rel="noreferrer" className="group block hover:opacity-80 transition-opacity">
                        <span className="text-sm font-bold text-ink group-hover:text-rule-3 transition-colors">Gina (Admin & Finance)</span>
                        <span className="text-sm text-ink/70 flex items-center gap-1.5 mt-0.5"><WhatsAppIcon className="w-3.5 h-3.5" />Message on WhatsApp</span>
                      </a>
                    </li>
                    <li>
                      <a href="https://wa.me/27722880800" target="_blank" rel="noreferrer" className="group block hover:opacity-80 transition-opacity">
                        <span className="text-sm font-bold text-ink group-hover:text-rule-3 transition-colors">Hymie (Business & Solutions)</span>
                        <span className="text-sm text-ink/70 flex items-center gap-1.5 mt-0.5"><WhatsAppIcon className="w-3.5 h-3.5" />Message on WhatsApp</span>
                      </a>
                    </li>
                    <li>
                      <a href="https://wa.me/27730849431" target="_blank" rel="noreferrer" className="group block hover:opacity-80 transition-opacity">
                        <span className="text-sm font-bold text-ink group-hover:text-rule-3 transition-colors">Jennifer (Community)</span>
                        <span className="text-sm text-ink/70 flex items-center gap-1.5 mt-0.5"><WhatsAppIcon className="w-3.5 h-3.5" />Message on WhatsApp</span>
                      </a>
                    </li>
                    <li>
                      <a href="https://wa.me/27651459012" target="_blank" rel="noreferrer" className="group block hover:opacity-80 transition-opacity">
                        <span className="text-sm font-bold text-ink group-hover:text-rule-3 transition-colors">Marc (Growth & Tech)</span>
                        <span className="text-sm text-ink/70 flex items-center gap-1.5 mt-0.5"><WhatsAppIcon className="w-3.5 h-3.5" />Message on WhatsApp</span>
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <MapPin className="w-5 h-5 text-rule-3 shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-bold text-ink text-sm">Headquarters</h4>
                  <p className="text-ink/70 text-sm">USA - Greater Philadelphia<br />RSA - Cape Town<br />Available globally</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
